import { Injectable, inject } from '@angular/core';
import {Observable, of, ReplaySubject} from 'rxjs';
import {map, tap, switchMap, filter} from 'rxjs/operators';

import {SessionService} from '@core';

import {JobIdInfo} from 'planner-shared';

import {SolverService} from '../solver/solver.service';
import {GenerateRoutesService} from '../generate-routes/generate-routes.service';
import {RoutesService} from '../routes/routes.service';
import {ProblemInstanceService} from '../problem-instance/problem-instance.service';

@Injectable({
    providedIn: 'root'
})
export class OptimizationService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly generateRoutesService = inject(GenerateRoutesService);
    private readonly solverService = inject(SolverService);
    private readonly sessionService = inject(SessionService);
    private readonly routesService = inject(RoutesService);

    private readonly jobIdInfoList$: ReplaySubject<JobIdInfo[]>;

    constructor() {
        this.jobIdInfoList$ = new ReplaySubject(1);
    }

    public initOptimization(): void {
        this.problemInstanceService
            .getProblemInstance()
            .pipe(
                switchMap(result => {
                    let jobId = result.problemInstance.jobId;
                    this.sessionService.setJobId(jobId);

                    if (!jobId && result.problemInstance.jobIdInfos) {
                        jobId = result.problemInstance.jobIdInfos[0]?.jobId;
                    }

                    return this.routesService.getRoutes(jobId).pipe(
                        map(routesResult => {
                            return {
                                routesResult,
                                jobIdInfos: result.problemInstance.jobIdInfos
                            };
                        })
                    );
                }),
                switchMap(result => {
                    this.sessionService.optimizationRunning = result.routesResult.optimizationRunning;
                    if (!result.routesResult.optimizationRunning && !result.routesResult.routes) {
                        return this.startOptimizationProcess();
                    }
                    return of(result.jobIdInfos);
                }),
                tap(jobIdInfos => this.jobIdInfoList$.next(jobIdInfos))
            )
            .subscribe();
    }

    public startOptimizationProcess(): Observable<JobIdInfo[]> {
        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result => this.stopOptimizationProcess().pipe(map(() => result.problemInstance))),
            switchMap(problemInstance =>
                this.generateRoutesService.generateRoutes().pipe(
                    tap(result => {
                        problemInstance.jobId = null;
                        problemInstance.jobIdInfos = result.jobIdInfoList;
                        this.sessionService.optimizationRunning = true;
                        this.sessionService.setJobId(null);
                        this.jobIdInfoList$.next(result.jobIdInfoList);
                    }),

                    map(result => result.jobIdInfoList)
                )
            )
        );
    }

    public stopOptimizationProcess(): Observable<void> {
        if (this.sessionService.optimizationRunning) {
            return this.solverService.stopSolvers().pipe(
                tap(() => (this.sessionService.optimizationRunning = false)),
                map(() => null)
            );
        }
        return of(null);
    }

    public getJobIdInfoWithoutChanges(): Observable<JobIdInfo> {
        return this.jobIdInfoList$.asObservable().pipe(
            filter(result => result != null),
            map(result =>
                result.find(i => i.jobConfigurationDeltaList == null || i.jobConfigurationDeltaList.length === 0)
            )
        );
    }

    public getJobIdInfosWithChanges(): Observable<JobIdInfo[]> {
        return this.jobIdInfoList$.asObservable().pipe(
            filter(result => result != null && result.length > 0),
            map(result => {
                const jobIdInfosWithChanges = result.filter(
                    i => i.jobConfigurationDeltaList == null || i.jobConfigurationDeltaList.length > 0
                );

                jobIdInfosWithChanges.forEach(jobIdInfo => {
                    jobIdInfo.jobConfigurationDeltaList.forEach(configuration => {
                        if (typeof configuration.newValue === 'boolean') {
                            switch (true) {
                            case (!configuration.newValue):
                                configuration.value = 'OFF';
                                break;
                            case (configuration.newValue):
                                configuration.value = 'ON';
                                break;
                            }
                        } else if (typeof configuration.newValue === 'number' && typeof configuration.previousValue === 'number') {
                            if (configuration.optimizationConfigurationName === 'patientTaskDurationAdjustmentPercentage') {
                                configuration.value =
                                    Math.round((-1 * configuration.previousValue + configuration.newValue) * 100) + '%';
                                configuration.optimizationConfigurationNameSecondPart = "patientTaskDurationAdjustmentPercentageSecondPart";
                            } else {
                                switch (true) {
                                case (configuration.newValue === 0):
                                    configuration.value = 'OFF';
                                    break;
                                case (configuration.newValue === 1):
                                    configuration.value = 'LOW';
                                    break;
                                case (configuration.newValue === 2):
                                    configuration.value = 'MEDIUM';
                                    break;
                                case (configuration.newValue >= 3):
                                    configuration.value = 'HIGH';
                                    break;
                                }
                            }
                        }
                    });
                });

                return jobIdInfosWithChanges;
            })
        );
    }
}
